/**
 * Mensagens finais do bolão (Copa 2026).
 *
 * Quando todos os 104 jogos têm resultado real lançado (72 grupos + 32
 * mata-mata, com pênaltis resolvidos para os empates), o bolão chegou
 * ao fim. Geramos uma mensagem personalizada para cada participante:
 *
 *  - 1º lugar: parabéns + valor recebido + nome do organizador
 *  - Premiados (não-1º): consolação + valor + parabéns
 *  - Sem prêmio: incentivo + chamada para o próximo bolão
 *
 * Tudo em texto limpo, pronto para copiar e colar no WhatsApp.
 */
import { JOGOS_COPA_2026 } from '../data/copaMundo2026';
import { JOGOS_MATA_MATA } from '../data/mataMataCopa2026';
import {
  placarValido,
  placarEmpatadoMM,
  type PlacaresMap,
  type VencedoresPenaltisMMMap,
} from './copaCalculo';
import { formatarReais } from './copaPremiacao';

export interface DadosFimBolao {
  bolaoEncerrado: boolean;
  /** Quantos jogos do mata-mata já têm placar válido. */
  jogosMMComPlacar: number;
  /** Quantos jogos de MM empataram e ainda não têm vencedor de pênaltis marcado. */
  empatesSemPenaltis: number;
  /** Quantos jogos da fase de grupos já têm placar válido. */
  jogosGruposComPlacar: number;
}

/**
 * Avalia se o bolão chegou ao fim:
 *  - Todos os 72 jogos da fase de grupos com placar válido
 *  - Todos os 32 jogos do mata-mata com placar válido
 *  - Em qualquer empate do mata-mata, o vencedor de pênaltis está
 *    marcado (senão a final/3º não estariam decididas).
 */
export function avaliarFimBolao(
  resultadosReais: PlacaresMap,
  vencedoresPenaltisMM: VencedoresPenaltisMMMap = {}
): DadosFimBolao {
  let jogosGruposComPlacar = 0;
  for (const j of JOGOS_COPA_2026) {
    if (placarValido(resultadosReais[j.id])) jogosGruposComPlacar += 1;
  }

  let jogosMMComPlacar = 0;
  let empatesSemPenaltis = 0;
  for (const j of JOGOS_MATA_MATA) {
    const r = resultadosReais[j.id];
    if (!placarValido(r)) continue;
    jogosMMComPlacar += 1;
    if (placarEmpatadoMM(r) && !vencedoresPenaltisMM[j.id]) {
      empatesSemPenaltis += 1;
    }
  }

  const bolaoEncerrado =
    jogosGruposComPlacar === JOGOS_COPA_2026.length &&
    jogosMMComPlacar === JOGOS_MATA_MATA.length &&
    empatesSemPenaltis === 0;

  return { bolaoEncerrado, jogosMMComPlacar, empatesSemPenaltis, jogosGruposComPlacar };
}

export interface ParticipanteFinal {
  /** id do participante */
  id: string;
  nome: string;
  posicao: number;
  pontos: number;
  premioFinal: number;
  /** quem ficou em 1º (nome) — usado em mensagens de consolação */
  campeao: string;
}

export interface MensagemFinal {
  participanteId: string;
  nome: string;
  posicao: number;
  premioFinal: number;
  /** Categoria da mensagem: campeão, premiado (não-1º) ou sem prêmio. */
  categoria: 'campeao' | 'premiado' | 'sem-premio';
  /** Texto pronto para copiar/colar no WhatsApp. */
  texto: string;
}

function ehCampeaoUnico(p: ParticipanteFinal): boolean {
  return p.posicao === 1;
}

/** Frase fixa no fim de toda mensagem — convidando a conferir os palpites. */
const RODAPE_COMUM =
  'Você pode conferir os palpites de cada participante no link do bolão se ' +
  'tiver alguma dúvida sobre a pontuação.';

/** Assinatura fixa: "Um abraço, EDN - Bolão Copa do Mundo EDN 2026". */
const ASSINATURA = '\n\nUm abraço,\nEDN - Bolão Copa do Mundo EDN 2026';

/**
 * Gera o texto da mensagem final para um único participante.
 *
 * @param p dados do participante
 */
export function montarMensagemFinal(p: ParticipanteFinal): MensagemFinal {
  if (ehCampeaoUnico(p)) {
    const texto =
      `🏆 PARABÉNS, ${p.nome}!\n\n` +
      `Você foi o GRANDE CAMPEÃO do Bolão Copa do Mundo EDN 2026 com ${p.pontos} pontos! ` +
      `Acertou onde poucos acertaram e levou o prêmio máximo: ` +
      `${formatarReais(p.premioFinal)}.\n\n` +
      `Aproveite — você merece! ⚽🇧🇷\n\n` +
      RODAPE_COMUM +
      ASSINATURA;
    return {
      participanteId: p.id,
      nome: p.nome,
      posicao: p.posicao,
      premioFinal: p.premioFinal,
      categoria: 'campeao',
      texto,
    };
  }

  if (p.premioFinal > 0) {
    const texto =
      `🥈 ${p.nome}, não foi o título dessa vez — mas você ainda saiu ganhando!\n\n` +
      `Terminou em ${p.posicao}º lugar com ${p.pontos} pontos e levou ${formatarReais(p.premioFinal)}. ` +
      `O campeão da edição foi ${p.campeao}. ` +
      `Você esteve no páreo até o fim — segue afiado pro próximo bolão. ⚽\n\n` +
      RODAPE_COMUM +
      ASSINATURA;
    return {
      participanteId: p.id,
      nome: p.nome,
      posicao: p.posicao,
      premioFinal: p.premioFinal,
      categoria: 'premiado',
      texto,
    };
  }

  const texto =
    `${p.nome}, dessa vez não rolou — fica pra próxima!\n\n` +
    `Você terminou em ${p.posicao}º com ${p.pontos} pontos. O campeão foi ${p.campeao}. ` +
    `Obrigado por participar! No próximo bolão, com palpites mais afinados, quem sabe ` +
    `o nome no topo da tabela seja o seu? 👀⚽\n\n` +
    RODAPE_COMUM +
    ASSINATURA;
  return {
    participanteId: p.id,
    nome: p.nome,
    posicao: p.posicao,
    premioFinal: p.premioFinal,
    categoria: 'sem-premio',
    texto,
  };
}

/** Gera mensagens para todos os participantes ordenados por posição. */
export function montarMensagensFinais(
  participantes: ParticipanteFinal[]
): MensagemFinal[] {
  return participantes.map((p) => montarMensagemFinal(p));
}
